/**
 * Testimonials セクション。3 variants:
 *   quote_card  — 大きめ引用カード (1 件 / 2 件)
 *   multi_grid  — 3 件以上のグリッド
 *   compact_list — コンパクトな縦リスト
 */
import { MessagesSquare, Quote } from 'lucide-react';

import { ArrayField, ImageField, TextField, TextareaField } from './primitives';
import type { EditorProps, RendererProps, SectionDefinition } from './types';

interface Testimonial {
  quote: string;
  authorName: string;
  authorRole: string;
  authorCompany: string;
  avatarUrl: string;
}
interface TestimonialsConfig {
  title: string;
  subtitle: string;
  items: Testimonial[];
}

function TestimonialsEditor({ config, onChange, onCommit }: EditorProps) {
  const cfg = config as unknown as TestimonialsConfig;
  const set = (patch: Partial<TestimonialsConfig>) =>
    onChange({ ...config, ...patch });
  return (
    <div className="space-y-3">
      <TextField
        label="セクション見出し"
        value={cfg.title ?? ''}
        onChange={(v) => set({ title: v })}
        onCommit={onCommit}
      />
      <TextField
        label="サブタイトル"
        value={cfg.subtitle ?? ''}
        onChange={(v) => set({ subtitle: v })}
        onCommit={onCommit}
      />
      <ArrayField<Testimonial>
        label="お客様の声"
        items={cfg.items ?? []}
        onChange={(next) => set({ items: next })}
        emptyItem={() => ({
          quote: '',
          authorName: '',
          authorRole: '',
          authorCompany: '',
          avatarUrl: '',
        })}
        renderItem={(item, _i, update) => (
          <div className="space-y-2">
            <TextareaField
              label="引用文"
              value={item.quote}
              onChange={(v) => update({ quote: v })}
              onCommit={onCommit}
              rows={4}
            />
            <div className="grid grid-cols-2 gap-2">
              <TextField
                label="氏名"
                value={item.authorName}
                onChange={(v) => update({ authorName: v })}
                onCommit={onCommit}
              />
              <TextField
                label="役職"
                value={item.authorRole}
                onChange={(v) => update({ authorRole: v })}
                onCommit={onCommit}
              />
            </div>
            <TextField
              label="会社名"
              value={item.authorCompany}
              onChange={(v) => update({ authorCompany: v })}
              onCommit={onCommit}
            />
            <ImageField
              label="アバター画像"
              value={item.avatarUrl}
              onChange={(v) => {
                update({ avatarUrl: v });
                onCommit();
              }}
            />
          </div>
        )}
      />
    </div>
  );
}

function AuthorBlock({ t, compact }: { t: Testimonial; compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {t.avatarUrl ? (
        <img
          src={t.avatarUrl}
          alt={t.authorName}
          className={`rounded-full object-cover ${compact ? 'size-8' : 'size-10'}`}
        />
      ) : (
        <div
          className={`rounded-full bg-[var(--bg-hover)] ${
            compact ? 'size-8' : 'size-10'
          }`}
        />
      )}
      <div className="text-xs">
        <div className="font-semibold">{t.authorName}</div>
        <div className="text-[var(--text-muted)]">
          {[t.authorRole, t.authorCompany].filter(Boolean).join(' · ')}
        </div>
      </div>
    </div>
  );
}

function QuoteCardRenderer({ config }: RendererProps) {
  const v = config as unknown as TestimonialsConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div
        className={`grid gap-5 ${
          items.length === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2'
        }`}
      >
        {items.slice(0, 2).map((t, i) => (
          <div
            key={i}
            className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-6"
          >
            <Quote className="size-7 text-[var(--accent)] mb-3" />
            <p className="text-base leading-relaxed mb-4">{t.quote}</p>
            <AuthorBlock t={t} />
          </div>
        ))}
      </div>
    </section>
  );
}

function MultiGridRenderer({ config }: RendererProps) {
  const v = config as unknown as TestimonialsConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="text-center mb-8">
          {v.title && <h3 className="text-2xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t, i) => (
          <div
            key={i}
            className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-5"
          >
            <p className="text-sm leading-relaxed mb-4">{t.quote}</p>
            <AuthorBlock t={t} compact />
          </div>
        ))}
      </div>
    </section>
  );
}

function CompactListRenderer({ config }: RendererProps) {
  const v = config as unknown as TestimonialsConfig;
  const items = v.items ?? [];
  return (
    <section>
      {(v.title || v.subtitle) && (
        <div className="mb-6">
          {v.title && <h3 className="text-xl font-bold mb-1">{v.title}</h3>}
          {v.subtitle && (
            <p className="text-sm text-[var(--text-secondary)]">{v.subtitle}</p>
          )}
        </div>
      )}
      <ul className="divide-y divide-[var(--border)] border border-[var(--border)] rounded-lg">
        {items.map((t, i) => (
          <li key={i} className="p-4 flex gap-4 items-start">
            <Quote className="size-5 text-[var(--accent)] flex-shrink-0 mt-1" />
            <div className="flex-1 space-y-2">
              <p className="text-sm">{t.quote}</p>
              <AuthorBlock t={t} compact />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

const sample = (): Record<string, unknown> => ({
  title: 'お客様の声',
  subtitle: '導入企業からのフィードバック',
  items: [
    {
      quote:
        '導入後、業務にかかる時間が約 40% 削減されました。チーム全員が使いやすいと評価しています。',
      authorName: '田中 太郎',
      authorRole: '業務改革室長',
      authorCompany: '株式会社サンプル',
      avatarUrl: '',
    },
    {
      quote:
        '初期設定からサポートまで丁寧で、安心して導入できました。',
      authorName: '佐藤 花子',
      authorRole: 'IT 部長',
      authorCompany: 'テスト工業',
      avatarUrl: '',
    },
  ] as Testimonial[],
});

export const testimonialsDefinition: SectionDefinition = {
  type: 'testimonials',
  label: 'お客様の声',
  icon: MessagesSquare,
  description: '顧客からの引用 / フィードバック。',
  defaultVariant: 'quote_card',
  useCases: ['all'],
  variants: {
    quote_card: {
      label: '引用カード',
      description: '1-2 件を大きめのカードで',
      Renderer: QuoteCardRenderer,
      Editor: TestimonialsEditor,
      sample,
    },
    multi_grid: {
      label: 'マルチグリッド',
      description: '3 件以上をグリッドで',
      Renderer: MultiGridRenderer,
      Editor: TestimonialsEditor,
      sample: () => {
        const base = sample() as unknown as TestimonialsConfig;
        return {
          ...base,
          items: [
            ...base.items,
            {
              quote: '使い始めてすぐに業務改善を実感できました。',
              authorName: '鈴木 一郎',
              authorRole: '営業部長',
              authorCompany: 'デモ商事',
              avatarUrl: '',
            },
          ],
        } as unknown as Record<string, unknown>;
      },
    },
    compact_list: {
      label: 'コンパクトリスト',
      description: '縦に並べたコンパクト表示',
      Renderer: CompactListRenderer,
      Editor: TestimonialsEditor,
      sample,
    },
  },
};
